import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { StyleSheet, View, Text } from 'react-native';
import type L from 'leaflet';
import Supercluster from 'supercluster';
import {
  MapHandle,
  MapViewProps,
  DEFAULT_REGION,
  NORTH_AMERICA_BOUNDS,
  MIN_ZOOM,
  MAX_ZOOM,
} from './types';
import { useTheme, TYPOGRAPHY } from '../../theme';
import { createPrefixedLogger } from '@utils/logger';
import { useStructuresStore } from '../../store/structuresStore';
import {
  getLeaflet,
  injectLeafletStyles,
  applyLeafletPopupTheme,
  configureDefaultIcons,
  calculateZoom,
  applyBasemapFilter,
  LABEL_MIN_ZOOM,
  playArrivalRing,
} from './leafletHelpers';
import { createBasemapLayers } from './basemaps';
import { MapControls } from './MapControls';
import { planFitPadding, boundsInClearView } from './planFitPadding';
import { shouldFitOnPlanChange, type PlanEditorView } from './planFitDecision';
import {
  clearViewShift,
  panIntoClearView,
  shouldFly,
  type ClearViewInsets,
} from './clearViewCenter';
import {
  useUIStore,
  useRightDockInset,
  useLeftDockInset,
  selectLeftDockInset,
  selectRightDockInset,
  useObservationsVisible,
} from '../../store/uiStore';
import type { MapStyle } from '../../store/uiStore';
import { useServiceAreaPolygons } from './useServiceAreaPolygons.web';
import { useFhszOverlay } from './useFhszOverlay.web';
import { useFireHistoryOverlay } from './useFireHistoryOverlay.web';
import { useCommunityProgramOverlay } from './useCommunityProgramOverlay.web';
import { useDefensePlanLayer } from './useDefensePlanLayer.web';
import { useComposerPinLayer } from './useComposerPinLayer.web';
import { useDefensePlanDraw } from './useDefensePlanDraw.web';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { useMapOverlaysLayer } from './useMapOverlaysLayer.web';
import { usePlanMapPlacementLayer } from './usePlanMapPlacementLayer.web';
import { useUserLocation } from './useUserLocation.web';
import { useClusterIndex, ClusterInstance } from './useClusterIndex.web';
import { useMarkerLayer } from './useMarkerLayer.web';
import { MapPostHoverCard } from './MapPostHoverCard.web';
import { MapSlideshow } from './MapSlideshow.web';
import { selectSlides } from './slideshow/deck';
import { useSlideshowController, type SlideshowSnapshot } from './slideshow/useSlideshowController';
import { useSlideshowViewWatch } from './slideshow/useSlideshowViewWatch';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { useFieldMode } from '../../hooks/useFieldMode';
import { findStructureInShards, findUserAdded } from '../../store/structures/shardLoader';
import { usePostsStore } from '../../store/postsStore';
import type { Post } from '../../types';

const log = createPrefixedLogger('map:web');

// How far inside the clear rect a clicked marker must sit to count as in view.
const CLEAR_VIEW_MARGIN_PX = 24;

// Cluster index kept in a ref. Supercluster is a static index — we rebuild
// it whenever the loaded shard set changes (rarely) but never on pan or
// claim changes. Queries (`getClusters`) are cheap and return at most ~100
// features for a viewport regardless of how many total points are loaded.
// ClusterInstance (and StructurePointProps) are defined in useClusterIndex.web.

const WebMap = forwardRef<MapHandle, MapViewProps>(function WebMap(
  {
    initialRegion = DEFAULT_REGION,
    posts = [],
    lockBounds,
    lockMinZoom,
    onRegionChange,
    onStructurePress,
    onPostPress,
    showUserLocation = false,
  },
  ref
) {
  const { colors, isDark } = useTheme();
  const isDesktopShell = useDesktopShell();
  const fieldMode = useFieldMode();
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<L.Map | null>(null);
  // Held so the theme effect below can filter the basemap without touching
  // the marker panes.
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  // Transparent roads/place-name tiles drawn over the base in satellite mode.
  // Empty for the standard basemap, which bakes its own labels in.
  const referenceLayersRef = useRef<L.TileLayer[]>([]);
  // Which style the layers above were built for, so the swap effect can tell
  // a real change from a re-run.
  const renderedStyleRef = useRef<MapStyle | null>(null);
  // One layer holds every cluster/point marker currently drawn — we clear
  // and repopulate it imperatively on each render. This is orders of
  // magnitude faster than keeping tens of thousands of `L.Marker`s alive
  // and diffing them through React.
  const layerRef = useRef<L.LayerGroup | null>(null);
  const overlayLayerRef = useRef<L.LayerGroup | null>(null);
  // Stable markers keyed by feature identity. Clusters are keyed by a
  // rounded position + bucketed count (`c:<zoom>:<latBucket>:<lngBucket>:<countBucket>`)
  // so they survive supercluster index rebuilds — cluster_ids are
  // reassigned on every `.load()`, but at a given zoom two adjacent
  // rebuilds over overlapping data produce visually-identical clusters at
  // the same spots. Points are keyed by `p:<structureId>`.
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const superclusterRef = useRef<ClusterInstance | null>(null);
  const postMarkersRef = useRef<Map<string, L.Marker>>(new Map());
  // Track the last-rendered icon key (fill|category|hasDelta) per post
  // marker. Without this, an edit that flips a post's category or
  // before/after status wouldn't refresh the icon — the create-only loop
  // below skips ids that are already in postMarkersRef.
  const postMarkerIconKeyRef = useRef<Map<string, string>>(new Map());
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const initialInvalidateRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Hover preview state (web/desktop). `hovered` holds the post under the
  // cursor plus its container-pixel anchor; `containerSize` clamps the card
  // inside the map. Both are driven imperatively from Leaflet marker events.
  const [hovered, setHovered] = useState<{ post: Post; point: { x: number; y: number } } | null>(
    null
  );
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  // Observation presentation (MapSlideshow.web.tsx). The deck is a snapshot
  // of mapPosts + the map's current bounds, taken by buildSlideshowSnapshot
  // below — not a live subscription, so panning or zooming never changes
  // what is on screen. The controller owns the snapshot and everything
  // downstream of it (surface, position, autoplay, prefetch); this file owns
  // only the two things it cannot: the map's bounds, and the store read.
  const buildSlideshowSnapshotRef = useRef<() => SlideshowSnapshot | null>(() => null);
  const buildSlideshowSnapshot = useCallback(() => buildSlideshowSnapshotRef.current(), []);
  const slideshow = useSlideshowController({
    buildSnapshot: buildSlideshowSnapshot,
    isDesktopShell,
  });
  // Widest right-docked panel currently mounted (structure detail, defense
  // plan editor, or — via PresentationPanel's own DockedPanel, while
  // slideshow.stage === 'panel' — the observation presentation). 0 when
  // nothing is docked, which is always true below the desktop shell. Every
  // piece of right-anchored floating chrome this file owns (MapControls,
  // the hover card's clamp, focusOnBounds' padding) reads this instead of
  // learning about each panel by name.
  const rightDockInset = useRightDockInset();
  // Widest left-docked panel currently mounted (the defense plan
  // editor/viewer — the only left-docked surface today). The hover card's
  // clamp is the one piece of chrome this file owns that needs to know
  // about it; MapControls and focusOnBounds stay right-only.
  const leftDockInset = useLeftDockInset();
  // Pulled out because it is the one controller member this component calls:
  // the rest of the object is rebuilt every render, and depending on it
  // would make handleOpenSlideshow unstable.
  const openSlideshow = slideshow.open;
  // Same reasoning — used by the plan-panel-open effect below to end a live
  // presentation, since the two docked surfaces can't show at once.
  const closeSlideshow = slideshow.close;

  // Store reads. None of these flow back into React render of this component
  // except via refs / imperative effects — the map never re-renders on a
  // claim update or a pan.
  const structuresByShard = useStructuresStore((s) => s._structuresByShard);
  const userAddedStructures = useStructuresStore((s) => s.userAddedStructures);
  const shardOrderKey = useStructuresStore((s) => s._loadedShardOrder.join(','));
  // Cheap signature for the user-added list — bumps when a structure is
  // added, removed, or edited (id + updatedAt + centroid), so an address or
  // location fix rebuilds the cluster index without a reload. The list is
  // global (no area filter) and capped small, so a string fold is fine.
  const userAddedSignature = useStructuresStore((s) =>
    s.userAddedStructures.reduce(
      (acc, st) =>
        `${acc}|${st.id}:${st.updatedAt?.toMillis() ?? 0}:${st.centroid.latitude}:${st.centroid.longitude}`,
      'ua'
    )
  );
  const claimsByStructureId = useStructuresStore((s) => s.claimsByStructureId);
  const selectedStructure = useStructuresStore((s) => s.selectedStructure);
  const structurePostBadges = usePostsStore((s) => s.structurePostBadges);
  // The only store read here that intentionally re-renders the map: a basemap
  // change is a deliberate user action, not a pan or a claim update.
  const mapStyle = useUIStore((s) => s.mapStyle);
  // The post already open in the right-docked panel — its hover card is
  // suppressed below (panel-system.md review, note 5) so the same photo
  // doesn't show twice at once.
  const dockedPostId = useUIStore((s) => s.dockedPostId);
  // Read to tint the layers button only — each overlay's own rendering is
  // driven by its hook reading the store directly, not by these values.
  const fhszOverlayEnabled = useUIStore((s) => s.fhszOverlayEnabled);
  const fireHistoryEnabled = useUIStore((s) => s.fireHistoryEnabled);
  // Unlike the two above, these are also passed into their hook: one
  // parametrized hook renders both community program layers, so which one is
  // on has to come in as an argument rather than be read from the store.
  const firewiseEnabled = useUIStore((s) => s.firewiseEnabled);
  const fireSafeCouncilsEnabled = useUIStore((s) => s.fireSafeCouncilsEnabled);
  const openSheet = useUIStore((s) => s.openSheet);
  // Desktop-shell Layers panel (see handleOpenLayers below). layersPanelOpen
  // also drives the button's aria-expanded so the toggle reads correctly
  // whether it opened the sheet or the panel.
  const layersPanelOpen = useUIStore((s) => s.layersPanelOpen);
  const toggleLayersPanel = useUIStore((s) => s.toggleLayersPanel);
  // One-shot request from MapLayersSheet's "Present observations" row (that
  // sheet is mounted globally with no access to the Leaflet instance this
  // component owns) — see uiStore.slideshowOpenRequestId.
  const slideshowOpenRequestId = useUIStore((s) => s.slideshowOpenRequestId);
  // Same shape, the closing direction — see uiStore.slideshowCloseRequestId.
  const slideshowCloseRequestId = useUIStore((s) => s.slideshowCloseRequestId);
  // Same one-shot pattern for the "Draw a defense plan" entry points (the
  // pill and the Layers sheet row) — see uiStore.planPanelOpenRequestId.
  const planPanelOpenRequestId = useUIStore((s) => s.planPanelOpenRequestId);
  // Drives the "bring the plan into view" effect below — whether the
  // editor is open, and the owner's own plan it's editing (the plan
  // useDefensePlanLayer.web.ts renders while no other plan is being viewed).
  const planPanelOpen = useUIStore((s) => s.planPanelOpen);
  const ownPlanId = useDefensePlansStore((s) => s.ownPlan?.id ?? null);

  // Grabbing the latest claims + selection via refs so the render function
  // can run synchronously inside Leaflet events without stale closure bugs.
  const claimsRef = useRef(claimsByStructureId);
  const badgesRef = useRef(structurePostBadges);
  const selectedIdRef = useRef<string | null>(selectedStructure?.id ?? null);
  claimsRef.current = claimsByStructureId;
  badgesRef.current = structurePostBadges;
  selectedIdRef.current = selectedStructure?.id ?? null;

  const colorsRef = useRef(colors);
  colorsRef.current = colors;
  const onStructurePressRef = useRef(onStructurePress);
  onStructurePressRef.current = onStructurePress;

  const renderClustersRef = useRef<() => void>(() => {});
  // Surgical per-id restyle for claim / selection changes. Updates only the
  // affected point marker's icon instead of re-running the full cluster
  // render over every visible feature.
  const restylePointsRef = useRef<(ids: Iterable<string>) => void>(() => {});

  // Bumped by every focusInClearView call; the arrival ring only plays for
  // the call that is still current when its move ends.
  const arriveTokenRef = useRef(0);
  const arriveCleanupRef = useRef<(() => void) | null>(null);

  // Insets to aim at: the caller's word for sides its own action is about to
  // change (a dock still sliding in has not registered in uiStore yet), and
  // what is registered right now for the rest.
  const resolveInsets = (over?: Partial<ClearViewInsets>): ClearViewInsets => {
    const s = useUIStore.getState();
    return {
      left: over?.left ?? selectLeftDockInset(s),
      right: over?.right ?? selectRightDockInset(s),
      top: over?.top ?? s.mapTopInset,
      bottom: over?.bottom ?? 0,
    };
  };

  useImperativeHandle(ref, () => ({
    focusOnLocation: (latitude, longitude, zoom = 15) => {
      leafletMapRef.current?.setView([latitude, longitude], zoom);
    },
    focusInClearView: (latitude, longitude, zoom, opts) => {
      const map = leafletMapRef.current;
      if (!map) return;
      const L = getLeaflet();
      const reduced =
        typeof window !== 'undefined' &&
        !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      // Fresh size: this can fire right after a panel resized the container.
      map.invalidateSize({ animate: false });
      const size = map.getSize();
      const shift = clearViewShift(size, resolveInsets(opts?.insets));
      // Project at the destination zoom, shift so the target lands at the
      // center of the clear rect, and come back to a lat/lng.
      const target = L.latLng(latitude, longitude);
      const dest = map.unproject(map.project(target, zoom).add([shift.x, shift.y]), zoom);
      const distance = map.project(map.getCenter(), zoom).distanceTo(map.project(dest, zoom));
      const fly =
        !reduced && opts?.animate !== false && shouldFly(distance, size, zoom - map.getZoom());

      // Only the newest call may ring; an interrupted one is dropped.
      const token = ++arriveTokenRef.current;
      arriveCleanupRef.current?.();
      arriveCleanupRef.current = null;
      if (!reduced) {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const off = () => {
          map.off('moveend', onEnd);
          if (timer) clearTimeout(timer);
          if (arriveCleanupRef.current === off) arriveCleanupRef.current = null;
        };
        const onEnd = () => {
          off();
          if (token !== arriveTokenRef.current) return;
          playArrivalRing(map, target);
        };
        map.on('moveend', onEnd);
        // A move that never ends (map unmounted, view replaced) must not
        // leave the listener behind.
        timer = setTimeout(off, 3000);
        arriveCleanupRef.current = off;
      }

      if (fly) map.flyTo(dest, zoom, { duration: 0.6 });
      else map.setView(dest, zoom, { animate: false });
    },
    ensureInClearView: (latitude, longitude, opts) => {
      const map = leafletMapRef.current;
      if (!map) return;
      const delta = panIntoClearView(
        map.getSize(),
        resolveInsets(opts?.insets),
        map.latLngToContainerPoint([latitude, longitude]),
        CLEAR_VIEW_MARGIN_PX
      );
      // Already in clear view: leave the camera exactly where it is.
      if (delta.x === 0 && delta.y === 0) return;
      map.panBy([delta.x, delta.y]);
    },
    // Center on a post, then open its hover preview so the user can tell
    // which pin they arrived at. The jump is non-animated so the preview
    // can anchor synchronously with the final transform — an animated
    // flight would clear `hovered` via the movestart handler in init, and
    // waiting on moveend proved racy (stray moveends from resize/geolocate
    // land mid-flight and yield a stale container point).
    focusOnPost: (post, zoom = 15) => {
      const map = leafletMapRef.current;
      const container = mapRef.current;
      if (!map || !container || !post.location) return;
      const { latitude, longitude } = post.location;
      // The map's cached size is stale when this fires right after
      // dismissTo — the detail screen overlay resized the hidden map and
      // the ResizeObserver's corrective invalidateSize hasn't run yet — so
      // setView would center (and the preview would anchor) in the wrong
      // coordinate space. Recalculate synchronously first.
      map.invalidateSize({ animate: false });
      let attempts = 0;
      const show = () => {
        const p = map.latLngToContainerPoint([latitude, longitude]);
        // We just centered on the pin, so its container point must sit at
        // the DOM container's center (Leaflet's own getSize can't be
        // trusted here for the same staleness reason). If it doesn't, the
        // transform hasn't settled — re-anchor on the next settle,
        // best-effort after a few tries.
        const offCenter =
          Math.abs(p.x - container.clientWidth / 2) > 14 ||
          Math.abs(p.y - container.clientHeight / 2) > 14;
        if (offCenter && attempts++ < 3) {
          map.once('moveend', show);
          return;
        }
        setHovered({ post, point: { x: p.x, y: p.y } });
      };
      map.setView([latitude, longitude], zoom, { animate: false });
      show();
    },
    // Fit the view to a defense plan's bounds. Padding keeps the whole plan
    // clear of the docked panel and the on-screen controls, not just inside
    // the raw viewport edge; maxZoom stops a single-point plan (a lone
    // water tank) from zooming in absurdly close. planFitPadding adds the
    // docked panel and the phone plan card to the edges they cover.
    focusOnBounds: (bounds, paddingPx = 64) => {
      const map = leafletMapRef.current;
      if (!map) return;
      map.fitBounds(
        [
          [bounds.s, bounds.w],
          [bounds.n, bounds.e],
        ],
        { ...planFitPadding(paddingPx), maxZoom: 17 }
      );
    },
  }));

  // --- Init ---
  useEffect(() => {
    const init = () => {
      try {
        injectLeafletStyles();
        const L = getLeaflet();
        configureDefaultIcons();

        if (!mapRef.current || leafletMapRef.current) return;

        const map = L.map(mapRef.current, {
          zoomControl: false,
          maxBounds: L.latLngBounds(
            [NORTH_AMERICA_BOUNDS.south, NORTH_AMERICA_BOUNDS.west],
            [NORTH_AMERICA_BOUNDS.north, NORTH_AMERICA_BOUNDS.east]
          ),
          maxBoundsViscosity: 1.0,
          minZoom: MIN_ZOOM,
          maxZoom: MAX_ZOOM,
        }).setView(
          [initialRegion.latitude, initialRegion.longitude],
          calculateZoom(initialRegion.latitudeDelta)
        );

        // Using our own zoom/locate button stack rendered as React below —
        // keeps all three buttons aligned and above the floating nav bar
        // without having to fight Leaflet's pane z-index.
        // Read the style off the store rather than the subscribed value: this
        // effect runs once, and hydration of the persisted choice may not have
        // landed yet. The swap effect below picks up whatever arrives later.
        const initialStyle = useUIStore.getState().mapStyle;
        const basemap = createBasemapLayers(L, initialStyle);
        basemap.base.addTo(map);
        basemap.reference.forEach((layer) => layer.addTo(map));
        tileLayerRef.current = basemap.base;
        referenceLayersRef.current = basemap.reference;
        renderedStyleRef.current = initialStyle;

        const layer = L.layerGroup();
        layer.addTo(map);
        layerRef.current = layer;

        const overlayLayer = L.layerGroup();
        overlayLayer.addTo(map);
        overlayLayerRef.current = overlayLayer;

        // Empty cluster index — populated by the shard-change effect below.
        // Load an empty feature set so supercluster's internal trees are
        // initialized; without this, `getClusters()` throws if the map
        // fires `moveend` (e.g. programmatic `setView` from geolocation)
        // before the deferred shard-commit load has run.
        superclusterRef.current = new Supercluster({
          radius: 60,
          maxZoom: MAX_ZOOM,
          minPoints: 3,
        });
        superclusterRef.current.load([]);

        const syncLabelVisibility = () => {
          const container = map.getContainer();
          container.classList.toggle('hide-structure-labels', map.getZoom() < LABEL_MIN_ZOOM);
          // Nothing in the app reads this. It is the only way an audit
          // running in the page can tell whether a click zoomed the map, and
          // an attribute renders nothing.
          container.dataset.zoom = String(map.getZoom());
        };

        const emitRegion = () => {
          const center = map.getCenter();
          const bounds = map.getBounds();
          onRegionChange?.({
            latitude: center.lat,
            longitude: center.lng,
            latitudeDelta: bounds.getNorth() - bounds.getSouth(),
            longitudeDelta: bounds.getEast() - bounds.getWest(),
          });
        };

        // render and restylePoints are populated by useMarkerLayer (called
        // below, after isLoaded becomes true). The map event listeners call
        // them via renderClustersRef / restylePointsRef so they always invoke
        // the latest version without re-registering these event handlers.
        syncLabelVisibility();
        map.on('zoomend', () => {
          syncLabelVisibility();
          renderClustersRef.current();
        });
        map.on('moveend', () => {
          emitRegion();
          renderClustersRef.current();
        });
        // Drop the hover preview the moment a pan/zoom starts so it never
        // sticks to a stale pixel. (mouseout covers most cases, but a
        // keyboard/programmatic move wouldn't fire it.)
        map.on('movestart', () => setHovered(null));
        map.on('zoomstart', () => setHovered(null));
        map.whenReady(() => {
          emitRegion();
          renderClustersRef.current();
        });

        leafletMapRef.current = map;
        setIsLoaded(true);

        // Leaflet's classic "grey tiles" pathology: the container's
        // height is sometimes 0 at mount (fonts not yet loaded, tab bar
        // shimming layout, web layout still settling) and Leaflet won't
        // recalculate on its own. Fire a single deferred invalidate +
        // observe container resizes so the map repaints when the
        // viewport or surrounding chrome changes.
        const scheduleInvalidate = () => {
          if (!leafletMapRef.current) return;
          leafletMapRef.current.invalidateSize();
        };
        // Seed the container size for hover-card clamping.
        setContainerSize({
          width: mapRef.current.clientWidth,
          height: mapRef.current.clientHeight,
        });
        const initialInvalidate = setTimeout(scheduleInvalidate, 250);
        let resizeObserver: ResizeObserver | null = null;
        if (mapRef.current && typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver((entries) => {
            scheduleInvalidate();
            const rect = entries[0]?.contentRect;
            if (rect) setContainerSize({ width: rect.width, height: rect.height });
          });
          resizeObserver.observe(mapRef.current);
        }
        resizeObserverRef.current = resizeObserver;
        initialInvalidateRef.current = initialInvalidate;
      } catch (err) {
        log.error('Failed to load map:', err);
        setError('Failed to load map');
      }
    };

    init();

    const postMarkers = postMarkersRef;
    return () => {
      postMarkers.current.forEach((marker) => marker.off());
      postMarkers.current.clear();
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (initialInvalidateRef.current) {
        clearTimeout(initialInvalidateRef.current);
        initialInvalidateRef.current = null;
      }
      if (layerRef.current) {
        layerRef.current.clearLayers();
        layerRef.current = null;
      }
      if (overlayLayerRef.current) {
        overlayLayerRef.current.clearLayers();
        overlayLayerRef.current = null;
      }
      if (leafletMapRef.current) {
        const map = leafletMapRef.current;
        // Leaflet 1.9's remove() deletes the panes but leaves the resize
        // handler's debounced moveend timer (_sizeTimer) running. If the
        // unmount races a window resize — e.g. crossing the desktop-shell
        // breakpoint remounts the map — that timer fires 'moveend' on the
        // dead map 200ms later and our handler's getCenter() throws
        // "_leaflet_pos of undefined". Drop all listeners and the timer
        // before remove().
        map.off();
        clearTimeout((map as unknown as { _sizeTimer?: ReturnType<typeof setTimeout> })._sizeTimer);
        map.remove();
        leafletMapRef.current = null;
      }
      // map.remove() detaches the tile layers; drop our handles so a
      // remount rebuilds them instead of reusing dead ones.
      tileLayerRef.current = null;
      referenceLayersRef.current = [];
      renderedStyleRef.current = null;
    };
    // Init-once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Basemap swap (illustrated map <-> satellite). Layers are torn down and
  // rebuilt rather than hidden and shown, so satellite's roads/labels layers
  // can't linger over the standard basemap, which carries its own labels.
  // `isLoaded` gates the first run until init has built the map.
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map || !isLoaded) return;
    if (renderedStyleRef.current === mapStyle) return;
    const L = getLeaflet();
    tileLayerRef.current?.remove();
    referenceLayersRef.current.forEach((layer) => layer.remove());
    const basemap = createBasemapLayers(L, mapStyle);
    basemap.base.addTo(map);
    basemap.reference.forEach((layer) => layer.addTo(map));
    tileLayerRef.current = basemap.base;
    referenceLayersRef.current = basemap.reference;
    renderedStyleRef.current = mapStyle;
    // The filter effect below runs straight after this one on the same
    // change, so the fresh base layer never paints a frame unfiltered.
  }, [mapStyle, isLoaded]);

  // Near-monochrome basemap (see applyBasemapFilter). `isLoaded` gates the
  // effect so it re-runs once the tile layer exists.
  useEffect(() => {
    applyBasemapFilter(tileLayerRef.current, isDark, mapStyle);
  }, [isDark, isLoaded, mapStyle]);

  // Leaflet popups (community program points) follow the app theme rather
  // than leaflet.css's fixed white. See applyLeafletPopupTheme.
  useEffect(() => {
    applyLeafletPopupTheme(colors);
  }, [colors]);

  // Apply pan/zoom lock to the selected service area. When `lockBounds` is
  // absent, fall back to the continent-wide bounds so the user can at least
  // pan within North America. `maxBoundsViscosity: 1.0` makes the edge
  // rubber-band feel fully rigid.
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map) return;
    const L = getLeaflet();
    if (!L) return;
    const bounds = lockBounds
      ? L.latLngBounds(
          [lockBounds.minLat, lockBounds.minLng],
          [lockBounds.maxLat, lockBounds.maxLng]
        )
      : L.latLngBounds(
          [NORTH_AMERICA_BOUNDS.south, NORTH_AMERICA_BOUNDS.west],
          [NORTH_AMERICA_BOUNDS.north, NORTH_AMERICA_BOUNDS.east]
        );
    map.setMaxBounds(bounds);
    map.setMinZoom(lockMinZoom ?? MIN_ZOOM);
    // If the user's current view is already outside the new lock, snap
    // them inside so they aren't stranded staring at empty map.
    if (!bounds.contains(map.getCenter())) {
      map.panInsideBounds(bounds, { animate: true });
    }
  }, [lockBounds, lockMinZoom]);

  // Cluster index rebuild — deferred to idle, cancelled on superseding shard
  // commits. Populates superclusterRef and triggers renderClustersRef.
  useClusterIndex({
    isLoaded,
    structuresByShard,
    userAddedStructures,
    shardOrderKey,
    userAddedSignature,
    superclusterRef,
    renderClustersRef,
  });

  const handlePostHover = useCallback((post: Post, point: { x: number; y: number }) => {
    setHovered({ post, point });
  }, []);
  const handlePostHoverEnd = useCallback(() => {
    setHovered(null);
  }, []);

  // Observations layer toggle (Layers sheet, off by default while a defense
  // plan is open — client ask, Sept 2026). Drop any open hover card the
  // moment the layer goes dark: its marker is about to be torn off the map,
  // and a card with nothing behind it would be stranded on screen.
  const observationsVisible = useObservationsVisible();
  useEffect(() => {
    if (!observationsVisible) setHovered(null);
  }, [observationsVisible]);

  // Marker layer — populates renderClustersRef / restylePointsRef and owns
  // the claim/selection restyle + post-marker sync effects.
  useMarkerLayer({
    leafletMapRef,
    layerRef,
    markersRef,
    postMarkersRef,
    postMarkerIconKeyRef,
    superclusterRef,
    renderClustersRef,
    restylePointsRef,
    claimsRef,
    badgesRef,
    selectedIdRef,
    colorsRef,
    onStructurePressRef,
    isLoaded,
    posts,
    claimsByStructureId,
    structurePostBadges,
    selectedStructure,
    colors,
    onPostPress,
    onPostHover: handlePostHover,
    onPostHoverEnd: handlePostHoverEnd,
    visible: observationsVisible,
  });

  useServiceAreaPolygons({ mapRef: leafletMapRef, overlayLayerRef, isLoaded });
  useFhszOverlay({ mapRef: leafletMapRef, overlayLayerRef, isLoaded });
  useFireHistoryOverlay({ mapRef: leafletMapRef, overlayLayerRef, isLoaded });
  // The two community program layers. Own panes (not overlayLayerRef): they
  // are clustered point layers with clickable popups, not static area fills
  // toggled en masse, and neither is scoped to a service area.
  useCommunityProgramOverlay({
    mapRef: leafletMapRef,
    isLoaded,
    programId: 'firewise',
    enabled: firewiseEnabled,
    color: colors.mapMarkerCommunity,
  });
  useCommunityProgramOverlay({
    mapRef: leafletMapRef,
    isLoaded,
    programId: 'fire-safe-councils',
    enabled: fireSafeCouncilsEnabled,
    color: colors.mapMarkerCommunity,
  });
  // Own pane (not overlayLayerRef) — a defense plan is live per-user data
  // with per-feature clicks, not a static area export toggled en masse, so
  // it diffs and adds/removes its own layers rather than sharing the group
  // the three overlays above clear and repaint into.
  useDefensePlanLayer({ mapRef: leafletMapRef, isLoaded });
  useDefensePlanDraw({ mapRef: leafletMapRef, isLoaded });
  // Draggable pin for the docked composer (panel-system.md Phase 3A) — see
  // useComposerPinLayer's doc comment.
  useComposerPinLayer({ mapRef: leafletMapRef, isLoaded });

  // Frame the plan when the owner switches to another of their plans in the
  // editor: the plan drawn on the map (useDefensePlanLayer.web.ts) is
  // otherwise wherever it was drawn, often well off screen. Opening the
  // editor does NOT fit (Oct 1 2026, Glenn panned to where he wanted to plan,
  // opened the tool, and the map flew to his old defenses); "Zoom to plan" in
  // the header menu asks for it explicitly, see planFitNonce below. The
  // decision is shouldFitOnPlanChange. A switch leaves the view alone when
  // the whole plan already sits in the part of the map the panels don't
  // cover.
  //
  // Until Sept 25 2026 this refit only when NO feature was in the viewport,
  // measured over the whole map including the strip under the left-docked
  // panel, and it ran in the same commit as the panel's mount, before the
  // panel had registered its width. Now the test is "all of it in the clear
  // area", and it runs a frame later, once DockedPanel's effect has
  // published its inset for planFitPadding to read.
  //
  // Keyed on the plan id, not the plan, so drawing a line out past the clear
  // area doesn't yank the map. Latest-wins by counter rather than a cleanup
  // cancel, so a quick second render can't swallow the fit (the map focus
  // channel's lesson).
  const planEditorViewRef = useRef<PlanEditorView>({ open: false, planId: null });
  const planFitRequestRef = useRef(0);
  useEffect(() => {
    const prev = planEditorViewRef.current;
    const next: PlanEditorView = { open: planPanelOpen, planId: ownPlanId };
    planEditorViewRef.current = next;
    const map = leafletMapRef.current;
    if (!isLoaded || !map || !shouldFitOnPlanChange(prev, next)) return;
    const request = ++planFitRequestRef.current;
    requestAnimationFrame(() => {
      if (request !== planFitRequestRef.current || !useUIStore.getState().planPanelOpen) return;
      const plan = useDefensePlansStore.getState().ownPlan;
      const bounds = plan?.id === ownPlanId && plan.features.length > 0 ? plan.bounds : null;
      if (!bounds) return;
      const latLngBounds: L.LatLngBoundsExpression = [
        [bounds.s, bounds.w],
        [bounds.n, bounds.e],
      ];
      if (boundsInClearView(map, latLngBounds, planFitPadding(16))) return;
      map.fitBounds(latLngBounds, { ...planFitPadding(), maxZoom: 17 });
    });
  }, [planPanelOpen, ownPlanId, isLoaded]);

  // "Zoom to plan" (the editor header's menu): fit the whole plan now, with
  // the same padding and zoom cap as every other plan fit, and without the
  // clear-view check above, since the owner asked.
  const planFitNonce = useUIStore((s) => s.planFitNonce);
  const handledPlanFitNonceRef = useRef(planFitNonce);
  useEffect(() => {
    if (planFitNonce === handledPlanFitNonceRef.current) return;
    handledPlanFitNonceRef.current = planFitNonce;
    const map = leafletMapRef.current;
    const bounds = useDefensePlansStore.getState().ownPlan?.bounds;
    if (!map || !bounds) return;
    map.fitBounds(
      [
        [bounds.s, bounds.w],
        [bounds.n, bounds.e],
      ],
      { ...planFitPadding(), maxZoom: 17 }
    );
  }, [planFitNonce]);

  // Plan-map overlays (moderator-imported wildfire plan pages, Sept 2026
  // phase 2): own pane, same reasoning as the defense plan layer above —
  // each overlay is diffed and drawn individually, not shared with the
  // static-export overlay group. The placement hook adds no image of its
  // own (see its doc comment); it only layers the drag gesture on top of
  // whatever the rendering hook already drew for the overlay being placed.
  useMapOverlaysLayer({ mapRef: leafletMapRef, isLoaded });
  usePlanMapPlacementLayer({ mapRef: leafletMapRef, isLoaded });
  const { handleLocateMe } = useUserLocation({
    mapRef: leafletMapRef,
    isLoaded,
    showUserLocation,
  });

  const handleZoomIn = useCallback(() => {
    leafletMapRef.current?.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    leafletMapRef.current?.zoomOut();
  }, []);

  // Desktop shell: Layers docks left instead of opening the centered sheet
  // (Glenn, Sept 23 2026 call — the sheet ran too tall and covered the
  // map). PR2 (the rail) moves the trigger itself off MapControls; this PR
  // only changes what pressing it does.
  const handleOpenLayers = useCallback(() => {
    if (isDesktopShell) {
      toggleLayersPanel();
      return;
    }
    openSheet('mapLayers');
  }, [isDesktopShell, openSheet, toggleLayersPanel]);

  // Synchronous centroid lookup for a structure-linked post with no own
  // location. Reads the store's in-memory shard cache directly (not a hook
  // subscription) — this only runs inside buildSlideshow, at click time.
  const structureCentroidLookup = useCallback((structureId: string) => {
    const state = useStructuresStore.getState();
    const fromShard = findStructureInShards(state._structuresByShard, structureId);
    if (fromShard) return fromShard.centroid;
    const fromUserAdded = findUserAdded(state.userAddedStructures, structureId);
    return fromUserAdded ? fromUserAdded.centroid : null;
  }, []);

  // The one place a deck is built, from the map's bounds right now. The
  // controller calls it on Present and on Update; the view watch calls it on
  // a settled pan to count what a fresh deck would hold, so the count in the
  // offer row is exactly the deck Update would produce.
  buildSlideshowSnapshotRef.current = () => {
    const map = leafletMapRef.current;
    if (!isLoaded || !map) return null;
    const b = map.getBounds();
    const bounds = {
      south: b.getSouth(),
      west: b.getWest(),
      north: b.getNorth(),
      east: b.getEast(),
    };
    return {
      slides: selectSlides({
        posts: usePostsStore.getState().mapPosts,
        bounds,
        structureCentroid: structureCentroidLookup,
      }),
      bounds,
    };
  };

  // Only while the panel is up: fullscreen hides the map, and a closed
  // presentation has nothing to compare against. No listener otherwise.
  const slideshowView = useSlideshowViewWatch({
    mapRef: leafletMapRef,
    active: isLoaded && slideshow.stage === 'panel',
    probe: buildSlideshowSnapshot,
    presentedBounds: slideshow.presentedBounds,
    presentedPhotoCount: slideshow.photoCount,
  });

  const handleOpenSlideshow = useCallback(() => {
    if (!isLoaded || !leafletMapRef.current) return;
    const opened = openSlideshow();
    // The presentation panel, a selected structure's DockedPanel and the
    // plan viewer are the same docked surface — none of them can show at
    // once. The map-tools rail's Present row (MapRailTools.web, Sept 2026
    // A2) stays mounted regardless of a selected structure, unlike the old
    // pill it replaced, so this is the one place that still has to clear a
    // selected structure on open. Decision: the presentation replaces
    // whichever of the two was up outright, and closing the presentation
    // does not restore it.
    if (isDesktopShell) {
      // Set in the same batch as the clears below, so the structure panel
      // sees the presentation taking its slot and goes at once instead of
      // sliding out under it. The stage effect further down sets the same
      // value a render later.
      if (opened) useUIStore.getState().setPresentationActive(true);
      useStructuresStore.getState().selectStructure(null);
      useUIStore.getState().setViewedPlanId(null);
      useUIStore.getState().closeDockedPost();
      useUIStore.getState().closeSheet();
      useUIStore.getState().closeComposerDock();
    }
  }, [isLoaded, isDesktopShell, openSlideshow]);

  useEffect(() => {
    if (!slideshowOpenRequestId) return;
    useUIStore.getState().clearSlideshowOpenRequest();
    handleOpenSlideshow();
  }, [slideshowOpenRequestId, handleOpenSlideshow]);

  // Same one-shot shape, the closing direction: the rail's Present row asks
  // the map to end the presentation the same way it asks to start one.
  useEffect(() => {
    if (!slideshowCloseRequestId) return;
    useUIStore.getState().clearSlideshowCloseRequest();
    closeSlideshow();
  }, [slideshowCloseRequestId, closeSlideshow]);

  // Mirrors the slideshow controller's own stage into uiStore so the rail's
  // Present row (mounted globally, no access to this controller) can read
  // whether the presentation is active without importing slideshow types.
  useEffect(() => {
    useUIStore.getState().setPresentationActive(slideshow.stage !== 'closed');
  }, [slideshow.stage]);

  // Cleared on unmount only — a separate effect with no deps, so this
  // cleanup fires once when the map itself goes away, not on every stage
  // change the effect above already handles (a shared cleanup there would
  // flip the mirror false-then-true on every transition between two
  // non-closed stages, e.g. panel -> fullscreen).
  useEffect(() => {
    return () => useUIStore.getState().setPresentationActive(false);
  }, []);

  // Dock arbitration (B6): the plan panel, the structure panel and the
  // presentation are one surface. Opening the plan panel clears the
  // selected structure and ends the presentation, then points the map at
  // the signed-in user's own plan (clearing any viewed plan) before opening
  // the panel — matches the slideshow-open effect above.
  useEffect(() => {
    if (!planPanelOpenRequestId) return;
    useUIStore.getState().clearPlanPanelOpenRequest();
    useStructuresStore.getState().selectStructure(null);
    closeSlideshow();
    useUIStore.getState().setViewedPlanId(null);
    useUIStore.getState().openPlanPanel();
  }, [planPanelOpenRequestId, closeSlideshow]);

  if (error) {
    return (
      <View style={[styles.container, styles.errorContainer]}>
        <Text style={[styles.errorText, { color: colors.danger }]}>{error}</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* data-testid, not a plain className: it's a hook for tests/audits to
          confirm this exact DOM node survives panel history push/pop
          (panel-system.md's "Deep links and back", Phase 4A — never mount a
          second Map), not a styling handle. */}
      <div ref={mapRef} data-testid="leaflet-map" style={styles.mapDiv as React.CSSProperties} />
      {/* Suppressed for the post already open in the right-docked panel —
          otherwise hovering its marker shows the same photo twice
          (panel-system.md review, note 5). */}
      {hovered && hovered.post.id !== dockedPostId && (
        <MapPostHoverCard
          post={hovered.post}
          point={hovered.point}
          // Docked panels overlay the map rather than resizing it, so the
          // container's real DOM size never shrinks when one opens — shave
          // the inset off here so the card's clamp treats the panel as
          // off-limits, the same way MapControls and the search bar do.
          containerSize={{
            width: Math.max(0, containerSize.width - rightDockInset),
            height: containerSize.height,
          }}
          leftInset={leftDockInset}
        />
      )}
      {/* Hidden only in fullscreen, which covers the whole map — the docked
          presentation panel now sits beside the controls like any other
          docked surface (rightDockInset), not on top of them. */}
      {slideshow.stage !== 'fullscreen' && (
        <MapControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onLocateMe={showUserLocation ? handleLocateMe : undefined}
          // Sept 2026 A2: the layers button moved to the map-tools rail on
          // the desktop shell — MapControls drops it there (the stack
          // itself already omits a button when the prop is absent). Phone/
          // tablet keep it; the rail doesn't exist below the desktop shell.
          // Field mode (phone website) drops it too.
          onOpenLayers={isDesktopShell || fieldMode ? undefined : handleOpenLayers}
          layersActive={mapStyle === 'satellite' || fhszOverlayEnabled || fireHistoryEnabled}
          layersExpanded={layersPanelOpen}
          rightInset={rightDockInset}
        />
      )}
      {/* "Present observations" itself is a row inside MapLegend's `above`
          slot (app/(tabs)/index.tsx), not rendered here — this component
          only owns what happens once uiStore.requestSlideshowOpen fires,
          whether that came from the pill or the Layers sheet's row. */}
      <MapSlideshow controller={slideshow} view={slideshowView} />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
    // See Z_INDEX_AUTO in utils/webStyles: RN Web gives every View z-index 0,
    // which would trap the hover card inside this container. The card and the
    // zoom controls have to clear the screen's search overlay above them.
    // This file is web-only, so no Platform fork.
    zIndex: 'auto' as unknown as number,
  },
  mapDiv: {
    width: '100%',
    height: '100%',
    // Leaflet's own panes run z-index 200-800 and its controls 800-1000. This
    // is a raw div, not an RN Web View, so it has no z-index of its own and
    // used to rely on the container above to box those panes in. The container
    // is now `auto` so the hover card can clear the screen's search overlay
    // (z-index 100), which left the panes free to paint over that overlay and
    // hide the search bar. Contain them here instead: one stacking context
    // around the whole map, sitting below the overlay, with the hover card and
    // the zoom controls outside it as siblings.
    position: 'relative',
    zIndex: 0,
  },
  errorContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    ...TYPOGRAPHY.body,
  },
});

export default WebMap;
